import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { WorkspaceService } from '@app/modules/workspaces/services/workspace.service'
import { ToastrService } from '@app/services/toastr.service'
import { AuthService } from '@auth/services/auth.service'
import { EventService } from '@events/services/event.service'
import { NbSpinnerModule } from '@nebular/theme'
import { WebsocketMessage } from '@venue-capacity/interfaces/websocket-message.interface'
import { WebsocketService } from '@venue-capacity/services/websocket.service'
import { Subject, takeUntil } from 'rxjs'
import { CapacityByDailyDatesComponent } from '../capacity-by-daily-dates/capacity-by-daily-dates.component'
import { CapacityCardComponent } from '../capacity-card/capacity-card.component'
import { EventInfoComponent } from '../event-info/event-info.component'
import { VenuCapacityFooterComponent } from '../venu-capacity-footer/venu-capacity-footer.component'
import { ZonesHeaderComponent } from '../zones-header/zones-header.component'
/**
 * Main VCD component
 */
@Component({
  selector: 'ngx-venue-capacity-page',
  templateUrl: './venue-capacity-page.component.html',
  styleUrls: ['./venue-capacity-page.component.scss'],
  standalone: true,
  imports: [CommonModule, CapacityCardComponent, CapacityByDailyDatesComponent, VenuCapacityFooterComponent, ZonesHeaderComponent, EventInfoComponent, NbSpinnerModule],
  providers: [EventService, WebsocketService]
})
export class VenueCapacityPageComponent implements OnInit, OnDestroy {
  @Input() eventId: number
  /**
   * Event model
   */
  event: any
  /**
   * Show daily visitors
   */
  showDaily: boolean
  /**
   * Current workspace slug
   */
  workspaceSlug: string
  /**
   * Current event slug
   */
  eventSlug: string
  /**
   * Current event zones
   */
  zones
  /**
   * Current selected zone
   */
  currentZone: any
  /**
   * Main event zone
   */
  mainZone: any
  /**
   * Is the component ready
   */
  ready: boolean
  /**
   * Are we in authenticated mode
   */
  authenticated: boolean
  /**
   * Are we in single unauthenticated mode
   */
  singleMode: boolean
  /**
   * Is the websocket connected
   */
  isLive: boolean
  public theme: { enable: boolean; logo: string }
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /**
   * Creates the component and injects it's dependencies
   * @param route ActivatedRoute
   * @param eventService EventService
   * @param auth AuthService
   */
  constructor(
    public route: ActivatedRoute,
    private eventService: EventService,
    private auth: AuthService,
    private websocket: WebsocketService,
    private toastrService: ToastrService,
    private workspaceService: WorkspaceService
  ) {}

  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.showDaily = false
    this.ready = false
    this.authenticated = false
    this.singleMode = true
    this.isLive = null
    this.zones = []
    this.workspaceSlug = this.route.snapshot.params.workspace_slug || this.auth.currentWorkspace.slug
    this.authenticated = this.auth.isLoggedIn && this.workspaceSlug === this.auth.currentWorkspace.slug
    this.eventId = this.eventId || this.route.snapshot.params.id
    this.singleMode = !!this.route.snapshot.params.zone_slug
    this.eventService
      .getVenueCapacityEvent(this.eventId)
      .pipe(takeUntil(this._destroy$))
      .subscribe((data) => {
        this.event = data
        // Work around to get the theme from the local storage => workspaces => options incase the event has no options
        //for testing purposes only
        this.getTheme()
        this.eventSlug = this.event.slug
        this.zones = (this.event.checkins as Array<any>)
          .filter((o) => !['global', 'global_virtual'].includes(o.slug) && !o.location_details?.service)
          .sort((a, b) => {
            const first = a.slug
            const second = b.slug
            if (first === 'main') {
              return -1
            }
            return a.weight == null || b.weight == null ? first - second : a.weight - b.weight
          })
        this.zones.find((o) => o.slug === 'main').name = 'Event Venue'
        this.mainZone = this.zones.find((o) => o.slug === 'main')
        this.currentZone = this.zones.find((o) => o.slug === this.route.snapshot.params?.zone_slug) || '#all$'
        this.ready = true
        this.establishWebsocketConnection()
      })
  }
  getTheme() {
    if (!this.authenticated) {
      this.workspaceService
        .getOrganizationTheme(this.event.workspace_id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (response) => {
            this.theme = response.theme
          },
          (error) => {
            this.theme = this.event.options?.theme ?? { enable: false, logo: '' }
          }
        )
      return
    }
    const workspace = this.auth.workspaces?.find((w) => w.id === this.event?.workspace_id)
    this.theme = workspace.options?.theme ? workspace.options.theme : this.event.options?.theme ? this.event.options.theme : { enable: false, logo: '' }
  }
  /**
   * Connect to the RTS websocket using the workspace slug and event slug,
   * and handle the websocket messages
   */
  establishWebsocketConnection() {
    this.websocket
      .connect(this.workspaceSlug, this.eventSlug)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (message: WebsocketMessage) => {
          switch (message.type) {
            case 'connection':
              this.isLive = true
              break
            case 'data':
              if (message.spot != 'main') this.zones.find((o) => o.slug === message.spot)['checkinCount'] = message.checkinCount
              break
            case 'error':
              this.isLive = false
              this.toastrService.error('Connection Error', message.error)
              break
            default:
              break
          }
        },
        (error) => {
          this.isLive = false
          this.toastrService.error('Connection Error', error.reason)
        }
      )
  }

  /**
   * Switches between daily and capacity mode
   * @param e boolean
   */
  updateCurrentView(e) {
    this.showDaily = e
  }
}
